<template>
  <div class="automation-configure-signal-settings-component" v-bind:class="{loaded, loading}">
    <div v-if="!loaded">
      <a-form-model-item label="Laden">
        <Skeleton class="skeleton" v-bind:loading="true" style="width: 100%; height: 32px;"></Skeleton>
      </a-form-model-item>
    </div>
    <div v-else>
      <AutomationDescriptionComponent v-bind:value="value"/>

      <a-form-model-item label="Naam van automation" v-bind:required="true">
        <a-input v-model="title" style="width: 100%" placeholder="Naam" v-bind:disabled="isGlobal"></a-input>
      </a-form-model-item>

      <a-form-model-item label="Omschrijving" v-bind:required="true">
        <a-textarea v-model="description" style="width: 100%"
                    placeholder="Omschrijving" v-bind:disabled="isGlobal"></a-textarea>
      </a-form-model-item>

      <div v-if="hasAutomationAction && hasGroupAction">
        <OnlyGroupActionSettingsComponent v-bind:settings="groupAction.settings"
                                          v-bind:form="value.automation_action_settings" v-bind:errors="{}"
                                          v-on:change="onSettingsChange" v-on:input="onSettingsChange"
                                          v-bind:loading="false"/>
      </div>

      <a-form-model-item label="Wil je eenmalig of automatisch data gaan verrijken over de selectie?"
                         v-bind:required="true">
        <dd-radio-group class="mb-md" v-model="runInterval" group-name="run-interval">
          <dd-radio value="never">Niet uitvoeren</dd-radio>
          <dd-radio value="once">Eenmalig</dd-radio>
          <dd-radio value="daily">Dagelijks</dd-radio>
          <dd-radio value="weekly">Wekelijks</dd-radio>
          <dd-radio value="monthly">Maandelijks</dd-radio>
        </dd-radio-group>
      </a-form-model-item>

      <a-collapse>
        <a-collapse-panel key="1" header="Geavanceerde instellingen">
          <div class="mb-md">
            <a-switch v-model="isRecurring" class="mr-md"/>
            Is dit een terugkerende actie?<br/>
            <small class="mt-sm" style="display: inline-block;">
              <i>
                Als je deze optie aanvinkt, wordt de actie automatisch opnieuw uitgevoerd wanneer
                gevonden resultaten zijn gewijzigd en opnieuw door de selectie worden gevonden.<br/>
                <br/>
                Wanneer deze optie uit staat wordt de actie alleen de eerste keer voor een gevonden
                resultaat uitgevoerd.
              </i>
            </small>
          </div>

          <div class="mb-md" v-if="false">
            <a-switch v-model="verify" class="mr-md"/>
            Wil je de verzendlijst controleren voor het daadwerkelijk versturen? <br/>

            <small class="mt-sm" style="display: inline-block;">
              <i>Als je deze optie aanvinkt, dien je zelf het akkoord te geven op uitgaande
                e-mails.</i>
            </small>
          </div>
        </a-collapse-panel>
      </a-collapse>
    </div>
  </div>
</template>

<script>
import { Skeleton } from "vue-loading-skeleton";
import { mapActions, mapGetters } from "vuex";
import store from "../store";
import AutomationDescriptionComponent from "./DescriptionComponent.vue";
import OnlyGroupActionSettingsComponent from "~/modules/group_action/OnlyGroupActionSettingsComponent.vue";
import groupActionStore from "~/modules/group_action/store";

export default {
  name: "AutomationConfigureDataEnrichmentSettingsComponent",
  components: {
    OnlyGroupActionSettingsComponent,
    AutomationDescriptionComponent,
    Skeleton
  },

  props: {
    value: {
      type: Object,
      required: true
    },
    isGlobal: {
      type: Boolean,
      required: false,
      default: false
    },
  },
  data() {
    return {
      loading: true,
      loaded: false,
      groupAction: null,
    };
  },
  mounted() {
    const STORE_KEY = "$_automation";
    // eslint-disable-next-line no-underscore-dangle
    if (!(STORE_KEY in this.$store._modules.root._children)) {
      this.$store.registerModule(STORE_KEY, store);
    }

    const GROUP_ACTION_STORE_KEY = "$_group_action";
    // eslint-disable-next-line no-underscore-dangle
    if (!(GROUP_ACTION_STORE_KEY in this.$store._modules.root._children)) {
      this.$store.registerModule(GROUP_ACTION_STORE_KEY, groupActionStore);
    }

    Promise.all([
      this.fetchAutomationActions(),
      this.fetchSearches(),
      this.fetchGroupAction()
    ])
        .finally(() => {
          this.loading = false;
          this.loaded = true;
        });
  },
  computed: {
    ...mapGetters({
      automationActions: "$_automation/automationActions",
      searches: "$_automation/searches",
      dynamicExtendingSearches: "$_automation/dynamicExtendingSearches",
    }),
    hasAutomationAction() {
      return this.selectedAutomationAction !== null && this.selectedAutomationAction !== undefined;
    },
    hasGroupAction() {
      return this.groupAction !== null && this.groupAction !== undefined;
    },
    runInterval: {
      get() {
        return this.value.run_interval;
      },
      set(value) {
        let automation = Object.assign({}, this.value, { run_interval: value });

        this.$emit("input", automation);
      },
    },
    isRecurring: {
      get() {
        return this.value.is_recurring;
      },
      set(value) {
        let automation = Object.assign({}, this.value, { is_recurring: value });

        this.$emit("input", automation);
      },
    },
    verify: {
      get() {
        return this.value.needs_verification;
      },
      set(value) {
        let automation = Object.assign({}, this.value, { needs_verification: value });

        this.$emit("input", automation);
      },
    },
    title: {
      get() {
        return this.value.title;
      },
      set(value) {
        let automation = Object.assign({}, this.value, { title: value });

        this.$emit("input", automation);
      },
    },
    description: {
      get() {
        return this.value.description;
      },
      set(value) {
        let automation = Object.assign({}, this.value, { description: value });

        this.$emit("input", automation);
      },
    },
    selectedAutomationAction() {
      return this.automationActions.find(action => action.id === this.value.automation_action_id);
    },
    selectedSearch() {
      return this.searches.find(search => search.id === this.value.search_id);
    },
    selectedAutomationActionName() {
      return this.selectedAutomationAction ? this.selectedAutomationAction.name : "";
    },
    selectedSearchDescription() {
      return this.selectedSearchName + " " + this.selectedExtendingSearchesDescription;
    },
    selectedDynamicSearches() {
      return this.dynamicExtendingSearches.filter((extendingSearch) => {
        return this.value.dynamic_extending_search_ids?.indexOf(extendingSearch.id) !== -1;
      });
    },
    selectedExtendingSearchesDescription() {
      if (this.selectedDynamicSearches && this.selectedDynamicSearches.length > 0) {
        const description = this.selectedDynamicSearches.map((extendingSearch) => {
          return extendingSearch.name;
        })
            .join(", ");

        return "met " + description;
      }

      return "";
    },
    selectedSearchName() {
      if (this.selectedSearch !== undefined) {
        return this.selectedSearch.name;
      }

      return "onbekend";
    },
  },
  methods: {
    ...mapActions({
      fetchAutomationActions: "$_automation/fetchAutomationActions",
      fetchSearches: "$_automation/fetchSearches",
      getGroupActionByAutomationActionId: "$_group_action/getGroupActionByAutomationActionId",
    }),
    fetchGroupAction() {
      this.getGroupActionByAutomationActionId(this.value.automation_action_id)
          .then(groupAction => {
            this.groupAction = groupAction;
          });
    },
    onSettingsChange(settings) {
      let automation = Object.assign({}, this.value, { automation_action_settings: settings });

      this.$emit("input", automation);
    }
  },
};
</script>
<style scoped lang="scss">
.automation-configure-signal-settings-component {
  position: relative;

  .ant-select {
    width: 100% !important;
    display: block !important;
  }
}
</style>
